/* eslint-disable react-native/no-inline-styles */
import { defaultInputProps } from '@Components/ProductDetailPanel/BuyFlow/CheckoutCore';
import { ProductPanelScrollMarker } from '@Components/ProductDetailPanel/product-panel-scroll-marker';
import useCheckoutPresenter from '@Screens/Checkout/presenter';
import useAuthPresenter from '@Screens/Main/authPresenter';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import styled from 'styled-components/native';

import OwnableTheme from '../../../assets/OwnableTheme';
import AuthAPI from '../../../gateways/auth';
import { PrimaryButton } from '../../Common/PrimaryButton';
import { Text } from '../../Common/Text';
import TextInput from '../../Common/TextInput';
import { useCheckoutContextState } from './CheckoutContextState';

import type { DigitalErrorDTO } from '~gateways/ownable.dtos';
import { useAnalyticsStore } from '~stores/analytics';
import { useAuthStore } from '~stores/authentication';
import { useCheckoutStore } from '~stores/checkout';
import { buttonLabelStyles } from './utils';

const StyledContainer = styled.View`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledAlignCenter = styled.View`
  align-items: center;
  justify-content: center;
`;

const StyledButtonContainer = styled.View`
  margin: 30px 0 10px;
  align-items: center;
`;

const SummaryStep: FC = () => {
  const {
    checkoutOrderId,
    isFeatureAvailableForCheckout,
    checkoutOrderToken,
    guestCheckoutEmail,
  } = useCheckoutStore();
  const { handleCancelCheckoutFlow, handleHistoryPress } =
    useCheckoutPresenter();

  const analyticsStore = useAnalyticsStore();
  const { currentUser } = useAuthStore();
  const { handleLoginSubmitAsync } = useAuthPresenter();
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [userHasAttemptedSignup, setUserHasAttemptedSignup] =
    useState<boolean>(false);
  const [registerSuccess, setRegisterSuccess] = useState<boolean>(false);
  const [registerErrors, setRegisterErrors] = useState<DigitalErrorDTO[]>([]);

  const { orderConfirmVisible, onCancelButton } = useCheckoutContextState();
  useEffect(() => {
    orderConfirmVisible.set(
      isFeatureAvailableForCheckout('ShowOrderConfirmation')
    );

    return () => {
      orderConfirmVisible.set(false);
    };
  }, []);

  orderConfirmVisible.set(
    isFeatureAvailableForCheckout('ShowOrderConfirmation')
  );

  return (
    <>
      {isFeatureAvailableForCheckout('ShowOrderConfirmation') && (
        <>
          <ProductPanelScrollMarker autoFocus />
          <StyledContainer>
            <Text
              gutterBottom={10}
              align='left'
              weight='bold'
              size='m'
              color='black'
            >
              Thanks for your order #{checkoutOrderId ?? ''}
            </Text>
            <Text gutterBottom={10} align='left' color='black'>
              We will contact you shortly to confirm delivery.
            </Text>
            {!currentUser?.id && (
              <>
                {!registerSuccess && (
                  <>
                    <Text gutterBottom={20} align='left' color='black'>
                      You can create an account to view the details of your
                      order by entering a password now
                    </Text>
                    <TextInput
                      {...defaultInputProps}
                      value={password}
                      placeholder='Your password'
                      secureTextEntry
                      gutterBottom={20}
                      onChange={(text) => {
                        setPassword(text);
                      }}
                      editable={!userHasAttemptedSignup}
                    />

                    <TextInput
                      {...defaultInputProps}
                      value={confirmPassword}
                      placeholder='Confirm password'
                      secureTextEntry
                      gutterBottom={50}
                      onChange={(text) => {
                        setConfirmPassword(text);
                      }}
                      editable={!userHasAttemptedSignup}
                    />

                    <StyledAlignCenter>
                      <PrimaryButton
                        text='Sign up'
                        color='black'
                        textColor={OwnableTheme.colors.white}
                        pressable={!userHasAttemptedSignup}
                        styleOverride={{
                          marginBottom: 10,
                          ...buttonLabelStyles,
                        }}
                        onPress={async () => {
                          if (
                            password !== confirmPassword ||
                            !checkoutOrderId ||
                            !checkoutOrderToken
                          ) {
                            return;
                          }
                          setUserHasAttemptedSignup(true);
                          const apiResponse = await AuthAPI.SignupFromOrder({
                            confirmPassword,
                            newPassword: password,
                            orderId: checkoutOrderId,
                            orderToken: checkoutOrderToken,
                          });
                          const { errors, success } = apiResponse;
                          if (success) {
                            analyticsStore.logSignupSubmit({
                              userId: currentUser?.id,
                              method: 'checkout',
                            });
                            await handleLoginSubmitAsync(
                              guestCheckoutEmail,
                              password
                            );
                            setRegisterSuccess(success);
                            setUserHasAttemptedSignup(false);
                            return;
                          }
                          setUserHasAttemptedSignup(false);
                          setRegisterErrors(errors);
                        }}
                      />
                    </StyledAlignCenter>
                    <Text
                      gutterBottom={10}
                      align='center'
                      color={OwnableTheme.colors.errorRed}
                      fontStyleOverride={buttonLabelStyles}
                    >
                      {Boolean(registerErrors.length) &&
                        registerErrors[0].userMessage}
                    </Text>
                  </>
                )}
              </>
            )}

            {currentUser?.id && (
              <Text gutterBottom={10} align='left' color='black'>
                You can check the details of your purchase{' '}
                <TouchableOpacity
                  activeOpacity={1.0}
                  onPress={() => {
                    handleHistoryPress();
                  }}
                >
                  <Text weight='bold' underline>
                    here.
                  </Text>
                </TouchableOpacity>
              </Text>
            )}

            <StyledButtonContainer>
              <PrimaryButton
                width={220}
                text='Close'
                color='white'
                textColor='black'
                borderColor='black'
                fontStyleOverride={buttonLabelStyles}
                onPress={() => {
                  onCancelButton();
                  handleCancelCheckoutFlow();
                }}
              />
            </StyledButtonContainer>
          </StyledContainer>
        </>
      )}
    </>
  );
};

export default observer(SummaryStep);
